/* eslint-disable react/require-default-props */
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import PropTypes from 'prop-types';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import { Button, Link } from '@one-thd/sui-atomic-components';
import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';
import { ProductPodUtils } from '@thd-olt-component-react/product-pod';
import { AddToQuoteDrawer } from './drawers/AddToQuoteDrawer';
import { useQuoteProduct } from './hooks/useQuotes';
import { dataModel } from '../dataModels/dataModel';
import { useCustomerInformation } from './hooks/useCustomerInformation';

const AddToQuote = ({
  itemId,
  quantity,
  fulfillmentLocation,
  fulfillmentMethod,
  disabled,
  products,
  productExceptions,
  buttonRef,
  buttonText,
  projectQuoteData,
  quoteStartTime,
  renderAsLink = false,
}) => {
  const { deliveryZip: deliveryZipCode, channel } = useContext(ExperienceContext);
  const projectZipCode = projectQuoteData?.projectZipCode;
  const isMobile = channel === 'mobile';
  const store = useStore();
  const [drawerShowing, setDrawerShowing] = useState(false);
  const quoteTimeTrackingRef = useRef({ startTime: null });
  const zipCode = projectZipCode ?? deliveryZipCode;

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('add-to-quote.ready');
  }, []);

  const customerInfo = useCustomerInformation();

  let item = null;
  if (itemId || products.length === 1) {
    item = {
      itemId: itemId || products[0]?.itemId,
      quantity: quantity || products[0]?.quantity
    };
  }

  const { quoteProduct } = useQuoteProduct({ itemId: item?.itemId });
  const product = quoteProduct?.product || {};

  let isDisabled;
  if (disabled !== null) {
    isDisabled = disabled;
  } else if (product && Object.keys(product).length > 0) {
    isDisabled = !podFulFillmentUtils.isATCEnabled(product) || podFulFillmentUtils.isAppliance(product);
  } else if (products?.length > 1) {
    isDisabled = products.some((currentProd) => {
      return (
        !currentProd || !podFulFillmentUtils.isATCEnabled(currentProd) || podFulFillmentUtils.isAppliance(currentProd)
      );
    });
  } else {
    isDisabled = true;
  }

  const fulfillmentMethodFromFulfillmentComponent = podFulFillmentUtils.getFulfillmentMethod(product);
  const fulfillmentLocationFromProductPodUtils = ProductPodUtils.getFulfillmentLocation({
    fulfillmentMethod: fulfillmentMethodFromFulfillmentComponent,
    store,
    product,
    deliveryZip: zipCode
  });

  const items = item
    ? [
      {
        ...item,
        fulfillmentMethod: fulfillmentMethod || fulfillmentMethodFromFulfillmentComponent,
        fulfillmentLocation: fulfillmentLocation || fulfillmentLocationFromProductPodUtils,
        identifiers: product?.identifiers,
        pricing: product?.pricing,
        media: product?.media,
      }
    ]
    : products.map((currentProduct) => ({
      itemId: currentProduct?.itemId,
      quantity: currentProduct?.quantity,
      fulfillmentMethod: podFulFillmentUtils.getFulfillmentMethod(currentProduct),
      fulfillmentLocation: ProductPodUtils.getFulfillmentLocation({
        fulfillmentMethod: podFulFillmentUtils.getFulfillmentMethod(currentProduct),
        store,
        product: currentProduct,
        deliveryZip: zipCode
      }),
      identifiers: currentProduct?.identifiers,
      pricing: currentProduct?.pricing
    }));

  const openDrawer = () => {
    if (isMobile && typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS?.trigger) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('add-to.mobile');
    }
    setDrawerShowing(true);
    quoteTimeTrackingRef.current.startTime = quoteStartTime ?? Math.floor(Date.now() / 1000);
  };

  const getAddToQuoteButton = () => {
    return renderAsLink
      ? (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          component="button"
          onClick={openDrawer}
          disabled={isDisabled}
          color={!isDisabled ? 'primary' : 'inactive'}
          data-testid="add-to-quote"
          data-component="AddToQuote"
        >
          { buttonText || 'Add to Quote' }
        </Link>
      )
      : (
        <Button
          variant="secondary"
          fullWidth
          onClick={openDrawer}
          disabled={isDisabled}
          data-testid="add-to-quote"
          data-component="AddToQuote"
          ref={buttonRef}
        >
          { buttonText || 'Add to Quote' }
        </Button>
      );
  };

  return (
    <>
      {getAddToQuoteButton()}
      {drawerShowing && (
        <QueryProvider
          cacheKey="add-to-quote-drawer"
          defaultVariables={{ svocId: customerInfo?.svocId }}
        >
          <AddToQuoteDrawer
            products={items}
            location={{
              localStoreId: store?.storeId,
              deliveryZip: zipCode
            }}
            drawerShowing={drawerShowing}
            setDrawerShowing={setDrawerShowing}
            projectQuoteData={projectQuoteData}
            productExceptions={productExceptions}
            quoteTimeTrackingRef={quoteTimeTrackingRef}
          />
        </QueryProvider>
      )}
    </>
  );
};

const safeHTMLButtonPropType = typeof HTMLButtonElement !== 'undefined'
  ? PropTypes.instanceOf(HTMLButtonElement)
  : () => null;

AddToQuote.propTypes = {
  disabled: PropTypes.bool,
  fulfillmentLocation: PropTypes.string,
  fulfillmentMethod: PropTypes.string,
  itemId: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      fulfillment: PropTypes.shape({}).isRequired,
      identifiers: PropTypes.shape({}).isRequired,
      pricing: PropTypes.shape({}).isRequired
    })
  ),
  productExceptions: PropTypes.arrayOf(
    PropTypes.shape({
      flag: PropTypes.string.isRequired,
      fulfillment: PropTypes.shape({}),
      identifiers: PropTypes.shape({}),
      pricing: PropTypes.shape({}),
      itemId: PropTypes.string,
      id: PropTypes.string,
      quantity: PropTypes.number
    })
  ),
  quantity: PropTypes.number,
  buttonRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: safeHTMLButtonPropType })
  ]),
  buttonText: PropTypes.string,
  projectQuoteData: PropTypes.shape({
    projectId: PropTypes.string,
    projectZipCode: PropTypes.string,
    projectName: PropTypes.string
  }),
  quoteStartTime: PropTypes.number,
  renderAsLink: PropTypes.bool
};

AddToQuote.defaultProps = {
  disabled: null,
  fulfillmentLocation: '',
  fulfillmentMethod: '',
  itemId: '',
  products: [],
  productExceptions: [],
  quantity: 0,
  buttonRef: null,
  buttonText: null,
  projectQuoteData: {},
  quoteStartTime: null
};

AddToQuote.displayName = 'AddToQuote';

AddToQuote.dataModel = extend(dataModel);

export { AddToQuote };
